import { useRef } from "react";
import { Download, Printer, FileText } from "lucide-react";
import html2canvas from "html2canvas";

function BusinessPlan({ plan, studentName, onClose, showActions = true }) {
  const planRef = useRef(null);

  const handleDownloadPDF = async () => {
    if (!planRef.current) return;
    
    try {
      const canvas = await html2canvas(planRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
      });
      
      const link = document.createElement("a");
      link.download = `${studentName.replace(/\s+/g, "_")}_Business_Workbook.png`;
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to download. Please try again.");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!plan) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📓</div>
        <p className="text-gray-600">Loading your workbook...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Action Buttons */}
      {showActions && (
        <div className="flex flex-wrap gap-3 mb-6 print:hidden">
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
            data-testid="download-workbook-btn"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            data-testid="print-workbook-btn"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium ml-auto"
              data-testid="close-workbook-btn"
            >
              Close
            </button>
          )}
        </div>
      )}

      {/* Business Workbook Document */}
      <div
        ref={planRef}
        className="bg-white rounded-2xl shadow-lg overflow-hidden"
        data-testid="business-workbook-document"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-400 to-teal-500 p-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileText className="w-10 h-10 text-white" />
            <span className="text-4xl">📓</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            My Business Workbook
          </h1>
          <p className="text-white/90 text-lg">
            {studentName}&apos;s Reflections & Answers
          </p>
          <p className="text-white/70 text-sm mt-2">
            {new Date(plan.generated_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric"
            })}
          </p>
        </div>

        {/* Progress Badge */}
        <div className="px-8 py-4 bg-green-50 border-b border-green-100">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl">📝</span>
            <span className="font-semibold text-green-800">
              {plan.is_complete 
                ? "All 7 Sessions Complete!" 
                : `${plan.completed_sessions} of ${plan.total_sessions} Sessions Completed`}
            </span>
          </div>
        </div>

        {/* Sections */}
        <div className="p-8 space-y-8">
          {plan.sections.map((section, index) => (
            <div
              key={section.id}
              id={section.id}
              className={`border-2 rounded-xl overflow-hidden ${
                section.completed 
                  ? "border-green-200 bg-green-50/30" 
                  : "border-gray-200 bg-gray-50/50"
              }`}
              data-testid={`plan-section-${section.id}`}
            >
              {/* Section Header */}
              <div className={`px-6 py-4 ${
                section.completed 
                  ? "bg-gradient-to-r from-green-100 to-emerald-100" 
                  : "bg-gray-100"
              }`}>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{section.icon}</span>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">
                      {index + 1}. {section.title}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Session {section.session_number}
                    </p>
                  </div>
                  {section.completed && (
                    <span className="ml-auto bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      ✓ Complete
                    </span>
                  )}
                </div>
              </div>

              {/* Section Content */}
              <div className="p-6">
                {section.completed && section.reflections.length > 0 ? (
                  <div className="space-y-4">
                    {section.reflections.map((reflection, rIndex) => (
                      <div key={rIndex} className="bg-white rounded-lg p-4 border border-gray-100">
                        <h3 className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">
                          {reflection.label}
                        </h3>
                        <p className="text-gray-800 leading-relaxed">
                          {reflection.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <p className="italic">
                      Complete Session {section.session_number} to add this section to your business plan.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-6 border-t border-gray-100">
          <div className="text-center">
            <p className="text-gray-600 mb-2">
              Created through the <strong>Lemonade Stand Business Plan</strong> Youth Entrepreneurship Program
            </p>
            <p className="text-sm text-gray-500">
              This business plan was built from {studentName}&apos;s reflections and learnings across all sessions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusinessPlan;
